import type { Skill } from '../skillsData';

export const cryptoskillPredictionMarketsAData: Skill[] = [
  {
    id: 'ant-oracle',
    name: 'ant-oracle',
    author: 'Ant Oracle',
    authorHandle: '@ant_oracle',
    tag: 'Skill',
    category: 'analytics',
    description: 'Decentralized prediction market oracle. Aggregate and verify outcome data from multiple sources for on-chain prediction market resolution.',
    downloads: 4200,
    stars: 312,
    installCommand: '> install the ant-oracle skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/prediction-markets/ant-oracle',
    usageExample: '> resolve prediction market\n> verify outcome data\n> submit oracle attestation',
    detailedDescription: 'Ant Oracle provides decentralized outcome verification for prediction markets. Aggregate data from multiple off-chain sources, verify consensus thresholds, and submit cryptographic attestations for on-chain market resolution with dispute escalation support.',
  },
  {
    id: 'bunyrs-alas-polymarket',
    name: 'bunyrs-alas-polymarket',
    author: 'Bunyrs Alas',
    authorHandle: '@bunyrs_alas',
    tag: 'Skill',
    category: 'trading',
    description: 'Polymarket trading automation with cross-market arbitrage detection and social signal correlation for prediction market alpha.',
    downloads: 6800,
    stars: 487,
    installCommand: '> install the bunyrs-alas-polymarket skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/prediction-markets/bunyrs-alas-polymarket',
    usageExample: '> scan polymarket arb opportunities\n> correlate social signals\n> auto-execute trades',
    detailedDescription: 'Bunyrs Alas Polymarket trading automation. Detect cross-market arbitrage between prediction markets and derivative exchanges, correlate social media sentiment signals with market pricing, and automate position entry/exit for prediction market alpha generation.',
  },
  {
    id: 'cortex-predictor',
    name: 'cortex-predictor',
    author: 'Cortex Labs',
    authorHandle: '@cortex_labs',
    tag: 'Skill',
    category: 'analytics',
    description: 'AI-powered prediction market forecaster. Machine learning models for event outcome prediction with confidence scoring and uncertainty quantification.',
    downloads: 12500,
    stars: 892,
    installCommand: '> install the cortex-predictor skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/prediction-markets/cortex-predictor',
    usageExample: '> predict election outcome\n> quantify forecast uncertainty\n> compare model ensembles',
    detailedDescription: 'Cortex Predictor deploys machine learning ensembles for prediction market forecasting. Train models on historical market data, news sentiment, polling aggregates, and macro indicators. Output calibrated probability distributions with explicit uncertainty quantification for informed market positioning.',
  },
  {
    id: 'cortex-underwriter',
    name: 'cortex-underwriter',
    author: 'Cortex Labs',
    authorHandle: '@cortex_labs',
    tag: 'Skill',
    category: 'analytics',
    description: 'Automated prediction market liquidity provision and market making. Intelligent order book management for binary outcome markets.',
    downloads: 8900,
    stars: 654,
    installCommand: '> install the cortex-underwriter skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/prediction-markets/cortex-underwriter',
    usageExample: '> provide liquidity to market\n> manage binary outcome spreads\n> auto-rebalance positions',
    detailedDescription: 'Cortex Underwriter automates prediction market liquidity provision. Deploy intelligent market-making strategies for binary outcome markets, dynamically adjust bid-ask spreads based on volatility and order flow, and rebalance inventory to minimize impermanent loss while maximizing fee capture.',
  },
  {
    id: 'football-odds-ai',
    name: 'football-odds-ai',
    author: 'Football Odds AI',
    authorHandle: '@football_odds_ai',
    tag: 'Skill',
    category: 'analytics',
    description: 'Sports prediction market intelligence for football. AI-driven odds analysis, team form modeling, and prediction market edge detection.',
    downloads: 15200,
    stars: 1103,
    installCommand: '> install the football-odds-ai skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/prediction-markets/football-odds-ai',
    usageExample: '> analyze football odds\n> predict match outcomes\n> find prediction market edge',
    detailedDescription: 'Football Odds AI combines sports analytics with prediction market intelligence. Model team form, player availability, and historical head-to-head data. Compare model predictions against prediction market pricing to identify positive expected value betting and trading opportunities.',
  },
  {
    id: 'forecast-parliament',
    name: 'forecast-parliament',
    author: 'Forecast Parliament',
    authorHandle: '@forecast_parliament',
    tag: 'Skill',
    category: 'analytics',
    description: 'Political event prediction market aggregator. Legislative voting forecasts, election outcome modeling, and policy impact prediction.',
    downloads: 7800,
    stars: 543,
    installCommand: '> install the forecast-parliament skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/prediction-markets/forecast-parliament',
    usageExample: '> forecast legislative vote\n> model election outcomes\n> predict policy impacts',
    detailedDescription: 'Forecast Parliament specializes in political event prediction markets. Aggregate polling data, legislative voting records, and expert forecasts to model election outcomes and policy passage probabilities. Track prediction market pricing against fundamental models for arbitrage opportunities in political markets.',
  },
  {
    id: 'foresight-agent4',
    name: 'foresight-agent4',
    author: 'Foresight AI',
    authorHandle: '@foresight_ai',
    tag: 'Skill',
    category: 'tools',
    description: 'Autonomous prediction market intelligence agent v4. Multi-source data fusion, narrative detection, and market timing for event contracts.',
    downloads: 22100,
    stars: 1567,
    installCommand: '> install the foresight-agent4 skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/prediction-markets/foresight-agent4',
    usageExample: '> run prediction market scan\n> detect emerging narratives\n> time market entries',
    detailedDescription: 'Foresight Agent v4 is an autonomous intelligence agent for prediction markets. Fuse data from news APIs, social media, on-chain signals, and traditional polling. Detect emerging narratives before they impact market pricing, and time position entries/exits using volatility regime analysis.',
  },
  {
    id: 'foresight-arena-agent',
    name: 'foresight-arena-agent',
    author: 'Foresight AI',
    authorHandle: '@foresight_ai',
    tag: 'Skill',
    category: 'tools',
    description: 'Multi-agent prediction market arena. Competing forecast agents submit predictions and are scored on accuracy for continuous model improvement.',
    downloads: 9500,
    stars: 712,
    installCommand: '> install the foresight-arena-agent skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/prediction-markets/foresight-arena-agent',
    usageExample: '> deploy competing agents\n> score prediction accuracy\n> evolve best models',
    detailedDescription: 'Foresight Arena Agent runs a competitive multi-agent prediction tournament. Deploy diverse forecasting agents with different methodologies, score their accuracy on resolved markets, and evolve the best-performing models through genetic algorithm optimization for superior prediction market performance.',
  },
  {
    id: 'fortune-skill',
    name: 'fortune-skill',
    author: 'Fortune Protocol',
    authorHandle: '@fortune_protocol',
    tag: 'Skill',
    category: 'analytics',
    description: 'Decentralized fortune telling and event prediction protocol. Crowdsourced wisdom aggregation with reputation-weighted consensus.',
    downloads: 5600,
    stars: 398,
    installCommand: '> install the fortune-skill skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/prediction-markets/fortune-skill',
    usageExample: '> crowdsource predictions\n> weight by reputation\n> aggregate consensus',
    detailedDescription: 'Fortune Skill implements decentralized wisdom aggregation for event prediction. Collect forecasts from a distributed network of predictors, weight contributions by historical accuracy and staked reputation, and output consensus probability estimates with confidence intervals for prediction market positioning.',
  },
  {
    id: 'graph-polymarket-mcp',
    name: 'graph-polymarket-mcp',
    author: 'Graph Protocol',
    authorHandle: '@graph_protocol',
    tag: 'Skill',
    category: 'tools',
    description: 'Polymarket subgraph MCP server. Query historical market data, volume analytics, and trader behavior via The Graph protocol.',
    downloads: 11200,
    stars: 834,
    installCommand: '> install the graph-polymarket-mcp skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/prediction-markets/graph-polymarket-mcp',
    usageExample: '> query polymarket subgraph\n> analyze volume trends\n> track trader behavior',
    detailedDescription: 'Graph Polymarket MCP provides subgraph access to Polymarket data via Model Context Protocol. Query historical market creation and resolution data, analyze volume and liquidity trends over time, track top trader behavior patterns, and identify market manipulation signals through on-chain analytics.',
  },
  {
    id: 'kalshi-agent',
    name: 'kalshi-agent',
    author: 'Kalshi Integration',
    authorHandle: '@kalshi_int',
    tag: 'Skill',
    category: 'trading',
    description: 'Kalshi prediction market trading agent. Event contract execution, portfolio management, and cross-exchange arb for regulated prediction markets.',
    downloads: 18700,
    stars: 1345,
    installCommand: '> install the kalshi-agent skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/prediction-markets/kalshi-agent',
    usageExample: '> trade kalshi events\n> manage event portfolio\n> find cross-exchange arb',
    detailedDescription: 'Kalshi Agent provides comprehensive access to Kalshi regulated prediction markets. Execute trades on event contracts, manage diversified prediction portfolios, and identify arbitrage opportunities between Kalshi and other prediction market venues with automated execution and risk management.',
  },
  {
    id: 'kalshi-integration',
    name: 'kalshi-integration',
    author: 'Kalshi Integration',
    authorHandle: '@kalshi_int',
    tag: 'Skill',
    category: 'analytics',
    description: 'Full Kalshi API integration. Market discovery, order management, portfolio tracking, and event outcome monitoring for regulated event contracts.',
    downloads: 13400,
    stars: 987,
    installCommand: '> install the kalshi-integration skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/prediction-markets/kalshi-integration',
    usageExample: '> discover kalshi markets\n> place event orders\n> track portfolio pnl',
    detailedDescription: 'Kalshi Integration provides full API coverage for the regulated prediction market platform. Discover active event markets across politics, economics, weather, and sports. Place and manage orders, track portfolio performance, and monitor event outcomes with automated settlement handling.',
  },
  {
    id: 'kalshi-prediction-agent',
    name: 'kalshi-prediction-agent',
    author: 'Kalshi AI',
    authorHandle: '@kalshi_ai',
    tag: 'Skill',
    category: 'tools',
    description: 'AI prediction agent for Kalshi markets. Autonomous event analysis, probability estimation, and trade execution on regulated event contracts.',
    downloads: 15600,
    stars: 1123,
    installCommand: '> install the kalshi-prediction-agent skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/prediction-markets/kalshi-prediction-agent',
    usageExample: '> analyze kalshi events\n> estimate probabilities\n> auto-execute trades',
    detailedDescription: 'Kalshi Prediction Agent deploys autonomous AI for regulated event contract trading. Analyze event parameters and historical base rates, estimate outcome probabilities using Bayesian models, and automatically execute trades when market pricing diverges from model predictions by predefined thresholds.',
  },
  {
    id: 'kalshi-prediction-markets',
    name: 'kalshi-prediction-markets',
    author: 'Kalshi Markets',
    authorHandle: '@kalshi_markets',
    tag: 'Skill',
    category: 'analytics',
    description: 'Comprehensive Kalshi prediction market suite. Market scanning, outcome modeling, and portfolio analytics for regulated event trading.',
    downloads: 19800,
    stars: 1456,
    installCommand: '> install the kalshi-prediction-markets skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/prediction-markets/kalshi-prediction-markets',
    usageExample: '> scan kalshi markets\n> model event outcomes\n> analyze portfolio',
    detailedDescription: 'Kalshi Prediction Markets provides a complete toolkit for regulated event contract trading. Scan all active Kalshi markets with filtering by category and expiration, model outcome probabilities using fundamental and technical approaches, and analyze prediction portfolio performance with sharpe ratio and drawdown tracking.',
  },
  {
    id: 'kalshi-prediction-tools',
    name: 'kalshi-prediction-tools',
    author: 'Kalshi Tools',
    authorHandle: '@kalshi_tools',
    tag: 'Skill',
    category: 'tools',
    description: 'Specialized prediction tools for Kalshi markets. Monte Carlo simulations, correlation matrices, and Kelly criterion position sizing.',
    downloads: 10200,
    stars: 765,
    installCommand: '> install the kalshi-prediction-tools skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/prediction-markets/kalshi-prediction-tools',
    usageExample: '> run monte carlo sim\n> build correlation matrix\n> calculate kelly size',
    detailedDescription: 'Kalshi Prediction Tools offers advanced analytical utilities for event contract trading. Run Monte Carlo simulations to model portfolio outcomes, build correlation matrices between related events, and calculate optimal position sizes using Kelly criterion and fractional Kelly variants with drawdown constraints.',
  },
  {
    id: 'kalshi-prediction',
    name: 'kalshi-prediction',
    author: 'Kalshi Predict',
    authorHandle: '@kalshi_predict',
    tag: 'Skill',
    category: 'analytics',
    description: 'Base prediction engine for Kalshi markets. Fundamental analysis, base rate calculation, and outcome probability estimation.',
    downloads: 14300,
    stars: 1056,
    installCommand: '> install the kalshi-prediction skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/prediction-markets/kalshi-prediction',
    usageExample: '> calculate base rate\n> estimate probability\n> run fundamental analysis',
    detailedDescription: 'Kalshi Prediction provides the foundational analysis engine for event contract markets. Calculate base rates from historical data, estimate outcome probabilities using Bayesian updating, and run fundamental analysis on event parameters to identify mispriced contracts in regulated prediction markets.',
  },
  {
    id: 'kit',
    name: 'kit',
    author: 'Kit Protocol',
    authorHandle: '@kit_protocol',
    tag: 'Skill',
    category: 'tools',
    description: 'Prediction market toolkit. Market creation, resolution management, and liquidity bootstrapping for custom event contracts.',
    downloads: 6700,
    stars: 498,
    installCommand: '> install the kit skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/prediction-markets/kit',
    usageExample: '> create prediction market\n> manage resolution\n> bootstrap liquidity',
    detailedDescription: 'Kit is a comprehensive prediction market toolkit. Create custom event markets with configurable parameters, manage outcome resolution with multi-source oracle verification, and bootstrap initial liquidity using automated market maker curves optimized for binary outcome events.',
  },
  {
    id: 'lkm-bomtron85',
    name: 'lkm-bomtron85',
    author: 'LKM Bomtron',
    authorHandle: '@lkm_bomtron',
    tag: 'Skill',
    category: 'trading',
    description: 'High-frequency prediction market trading system. Microsecond latency execution, order flow analysis, and momentum detection for event contracts.',
    downloads: 3200,
    stars: 234,
    installCommand: '> install the lkm-bomtron85 skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/prediction-markets/lkm-bomtron85',
    usageExample: '> run hft strategy\n> analyze order flow\n> detect momentum',
    detailedDescription: 'LKM Bomtron85 is a high-frequency trading system optimized for prediction markets. Execute trades with microsecond latency, analyze order flow imbalances for momentum signals, and deploy market-making strategies specifically tuned for the unique liquidity dynamics of binary outcome event contracts.',
  },
  {
    id: 'multiply-mcp',
    name: 'multiply-mcp',
    author: 'Multiply Protocol',
    authorHandle: '@multiply_protocol',
    tag: 'Skill',
    category: 'tools',
    description: 'Prediction market MCP server. Multi-platform event contract access via Model Context Protocol with unified query and execution interfaces.',
    downloads: 7800,
    stars: 587,
    installCommand: '> install the multiply-mcp skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/prediction-markets/multiply-mcp',
    usageExample: '> query prediction markets\n> execute cross-platform\n> aggregate positions',
    detailedDescription: 'Multiply MCP provides unified prediction market access via Model Context Protocol. Query markets across Polymarket, Kalshi, and other platforms through standardized interfaces, execute trades on multiple venues, and aggregate positions into a consolidated prediction portfolio view.',
  },
  {
    id: 'myriad-cli',
    name: 'myriad-cli',
    author: 'Myriad Markets',
    authorHandle: '@myriad_markets',
    tag: 'Skill',
    category: 'tools',
    description: 'Myriad prediction market command line interface. Market creation, trading, and resolution management for decentralized event contracts.',
    downloads: 5400,
    stars: 412,
    installCommand: '> install the myriad-cli skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/prediction-markets/myriad-cli',
    usageExample: '> create myriad market\n> trade event contracts\n> resolve outcomes',
    detailedDescription: 'Myriad CLI provides command-line access to Myriad decentralized prediction markets. Create custom event markets with flexible resolution criteria, trade outcome shares through automated market makers, and manage market resolution with decentralized oracle networks and dispute mechanisms.',
  },
  {
    id: 'odds-oracle',
    name: 'odds-oracle',
    author: 'Odds Oracle',
    authorHandle: '@odds_oracle',
    tag: 'Skill',
    category: 'analytics',
    description: 'Real-time odds aggregation oracle. Combine sportsbook, exchange, and prediction market pricing for true probability estimation.',
    downloads: 18900,
    stars: 1367,
    installCommand: '> install the odds-oracle skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/prediction-markets/odds-oracle',
    usageExample: '> aggregate odds\n> estimate true probability\n> detect pricing edges',
    detailedDescription: 'Odds Oracle aggregates pricing data from sportsbooks, betting exchanges, and prediction markets to estimate true outcome probabilities. Normalize odds across different formats, account for bookmaker margins, and identify positive expected value opportunities where prediction market pricing diverges from consensus odds.',
  },
  {
    id: 'olas-peer-polymarket',
    name: 'olas-peer-polymarket',
    author: 'Olas Protocol',
    authorHandle: '@olas_protocol',
    tag: 'Skill',
    category: 'trading',
    description: 'Peer-to-peer Polymarket trading via Olas. Direct outcome share transfers, pooled position management, and social trading features.',
    downloads: 8200,
    stars: 623,
    installCommand: '> install the olas-peer-polymarket skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/prediction-markets/olas-peer-polymarket',
    usageExample: '> p2p transfer shares\n> pool positions\n> follow top traders',
    detailedDescription: 'Olas Peer Polymarket enables peer-to-peer prediction market trading. Transfer outcome shares directly between wallets without AMM slippage, pool positions with friends for shared exposure, and follow successful traders with copy-prediction features for social event contract trading.',
  },
  {
    id: 'olas-polymarket-agent',
    name: 'olas-polymarket-agent',
    author: 'Olas Protocol',
    authorHandle: '@olas_protocol',
    tag: 'Skill',
    category: 'tools',
    description: 'Autonomous Olas agent for Polymarket. AI-driven market analysis, position management, and outcome prediction on decentralized event contracts.',
    downloads: 14500,
    stars: 1089,
    installCommand: '> install the olas-polymarket-agent skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/prediction-markets/olas-polymarket-agent',
    usageExample: '> deploy olas agent\n> analyze polymarket\n> auto-manage positions',
    detailedDescription: 'Olas Polymarket Agent is an autonomous AI agent built on the Olas protocol for prediction market trading. Analyze market conditions using on-chain and off-chain data, manage positions with dynamic sizing based on conviction levels, and predict event outcomes using ensemble models for decentralized event contract markets.',
  },
  {
    id: 'olas-polymarket-trader',
    name: 'olas-polymarket-trader',
    author: 'Olas Protocol',
    authorHandle: '@olas_protocol',
    tag: 'Skill',
    category: 'trading',
    description: 'Olas-powered Polymarket trading skill. Advanced order types, position hedging, and cross-market correlation trading on decentralized prediction markets.',
    downloads: 11200,
    stars: 845,
    installCommand: '> install the olas-polymarket-trader skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/prediction-markets/olas-polymarket-trader',
    usageExample: '> place advanced orders\n> hedge positions\n> trade correlations',
    detailedDescription: 'Olas Polymarket Trader provides advanced trading functionality for decentralized prediction markets. Place limit, stop-loss, and bracket orders on outcome shares, hedge positions across related markets, and exploit cross-market correlations for statistical arbitrage in event contract trading.',
  },
  {
    id: 'oracle-mind',
    name: 'oracle-mind',
    author: 'Oracle Mind',
    authorHandle: '@oracle_mind',
    tag: 'Skill',
    category: 'analytics',
    description: 'Predictive oracle intelligence. Consensus forecasting from distributed oracle networks with reputation-weighted outcome aggregation.',
    downloads: 7600,
    stars: 578,
    installCommand: '> install the oracle-mind skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/prediction-markets/oracle-mind',
    usageExample: '> query oracle consensus\n> weight by reputation\n> aggregate forecasts',
    detailedDescription: 'Oracle Mind harnesses distributed oracle networks for predictive intelligence. Collect forecasts from independent oracle nodes, weight contributions by historical accuracy and stake, and aggregate into consensus probability estimates with cryptographic verification for trustless prediction market resolution.',
  },
  {
    id: 'oraclemind-prediction',
    name: 'oraclemind-prediction',
    author: 'OracleMind',
    authorHandle: '@oraclemind_ai',
    tag: 'Skill',
    category: 'analytics',
    description: 'OracleMind prediction module. AI-enhanced outcome forecasting with on-chain data correlation and real-time event monitoring.',
    downloads: 9100,
    stars: 687,
    installCommand: '> install the oraclemind-prediction skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/prediction-markets/oraclemind-prediction',
    usageExample: '> forecast outcomes\n> correlate on-chain data\n> monitor events',
    detailedDescription: 'OracleMind Prediction deploys AI-enhanced forecasting for prediction markets. Correlate on-chain metrics like transaction volumes and wallet activity with event outcomes, monitor real-time news and social feeds for market-moving developments, and update probability estimates with Bayesian inference as new information arrives.',
  },
  {
    id: 'oraclemind-predictions',
    name: 'oraclemind-predictions',
    author: 'OracleMind',
    authorHandle: '@oraclemind_ai',
    tag: 'Skill',
    category: 'analytics',
    description: 'Batch prediction suite from OracleMind. Multi-event portfolio forecasting, covariance modeling, and systematic prediction market allocation.',
    downloads: 6700,
    stars: 512,
    installCommand: '> install the oraclemind-predictions skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/prediction-markets/oraclemind-predictions',
    usageExample: '> batch forecast events\n> model covariance\n> allocate systematically',
    detailedDescription: 'OracleMind Predictions provides batch forecasting for prediction market portfolios. Generate probability estimates for multiple simultaneous events, model outcome covariances to identify hedge relationships, and allocate capital systematically using mean-variance optimization adapted for binary outcome contracts.',
  },
  {
    id: 'oraclemind',
    name: 'oraclemind',
    author: 'OracleMind',
    authorHandle: '@oraclemind_ai',
    tag: 'Skill',
    category: 'tools',
    description: 'OracleMind prediction market platform. Market creation, automated trading, and outcome resolution with integrated AI forecasting.',
    downloads: 13400,
    stars: 1012,
    installCommand: '> install the oraclemind skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/prediction-markets/oraclemind',
    usageExample: '> create prediction market\n> auto-trade with AI\n> resolve with oracle',
    detailedDescription: 'OracleMind is a full-stack prediction market platform. Create custom event markets with flexible resolution criteria, deploy AI agents for automated trading based on fundamental and technical signals, and resolve markets using integrated oracle networks with dispute resolution and appeal mechanisms.',
  },
  {
    id: 'orba-prediction-markets',
    name: 'orba-prediction-markets',
    author: 'Orba Markets',
    authorHandle: '@orba_markets',
    tag: 'Skill',
    category: 'analytics',
    description: 'Orba prediction market analytics. Market sentiment tracking, volume anomaly detection, and whale wallet monitoring for event contracts.',
    downloads: 5800,
    stars: 437,
    installCommand: '> install the orba-prediction-markets skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/prediction-markets/orba-prediction-markets',
    usageExample: '> track market sentiment\n> detect volume anomalies\n> monitor whale wallets',
    detailedDescription: 'Orba Prediction Markets provides advanced analytics for event contract trading. Track market sentiment shifts through order flow analysis, detect volume anomalies that precede major price moves, and monitor whale wallet activity to anticipate informed trader positioning in prediction markets.',
  },
  {
    id: 'orba-prediction-whatsapp',
    name: 'orba-prediction-whatsapp',
    author: 'Orba Markets',
    authorHandle: '@orba_markets',
    tag: 'Skill',
    category: 'social',
    description: 'WhatsApp-integrated prediction market alerts. Real-time event updates, price alerts, and trading notifications via WhatsApp messaging.',
    downloads: 4200,
    stars: 318,
    installCommand: '> install the orba-prediction-whatsapp skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/prediction-markets/orba-prediction-whatsapp',
    usageExample: '> setup whatsapp alerts\n> get price notifications\n> receive event updates',
    detailedDescription: 'Orba Prediction WhatsApp delivers prediction market intelligence through WhatsApp messaging. Receive real-time event outcome updates, price movement alerts when markets shift beyond thresholds, and trading notifications for position management — all delivered directly to your WhatsApp for mobile-first prediction market monitoring.',
  },
  {
    id: 'otto-ai-prediction-markets',
    name: 'otto-ai-prediction-markets',
    author: 'Otto AI',
    authorHandle: '@otto_ai',
    tag: 'Skill',
    category: 'tools',
    description: 'Otto AI prediction market suite. Natural language market creation, automated trading, and outcome analysis for event contracts.',
    downloads: 12300,
    stars: 934,
    installCommand: '> install the otto-ai-prediction-markets skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/prediction-markets/otto-ai-prediction-markets',
    usageExample: '> create market via chat\n> auto-trade events\n> analyze outcomes',
    detailedDescription: 'Otto AI Prediction Markets enables natural language interaction with event contracts. Create prediction markets by describing events in plain English, deploy AI trading agents with conversational strategy configuration, and analyze resolved outcomes to improve future forecasting accuracy through feedback loops.',
  },
  {
    id: 'otto-ai-prediction',
    name: 'otto-ai-prediction',
    author: 'Otto AI',
    authorHandle: '@otto_ai',
    tag: 'Skill',
    category: 'analytics',
    description: 'Otto AI outcome prediction engine. Conversational probability estimation, scenario analysis, and confidence calibration for event forecasting.',
    downloads: 9800,
    stars: 745,
    installCommand: '> install the otto-ai-prediction skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/prediction-markets/otto-ai-prediction',
    usageExample: '> predict event outcome\n> analyze scenarios\n> calibrate confidence',
    detailedDescription: 'Otto AI Prediction provides conversational outcome forecasting. Describe events in natural language and receive probability estimates with explicit reasoning chains. Analyze alternative scenarios, calibrate confidence levels against historical accuracy, and export predictions to prediction market trading workflows.',
  },
  {
    id: 'otto-prediction-markets',
    name: 'otto-prediction-markets',
    author: 'Otto Protocol',
    authorHandle: '@otto_protocol',
    tag: 'Skill',
    category: 'trading',
    description: 'Otto prediction market trading protocol. Automated market making, liquidity optimization, and event-specific strategy deployment.',
    downloads: 8500,
    stars: 643,
    installCommand: '> install the otto-prediction-markets skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/prediction-markets/otto-prediction-markets',
    usageExample: '> deploy market maker\n> optimize liquidity\n> run event strategies',
    detailedDescription: 'Otto Prediction Markets is a trading protocol optimized for event contracts. Deploy automated market makers with inventory-aware pricing curves, optimize liquidity depth across multiple related markets, and deploy event-specific strategies like news-based momentum trading and post-event volatility harvesting.',
  },
  {
    id: 'polymarket-agent-mcp',
    name: 'polymarket-agent-mcp',
    author: 'Polymarket MCP',
    authorHandle: '@polymarket_mcp',
    tag: 'Skill',
    category: 'tools',
    description: 'Polymarket agent MCP server. AI agent integration for decentralized prediction markets via Model Context Protocol with full trading access.',
    downloads: 21300,
    stars: 1589,
    installCommand: '> install the polymarket-agent-mcp skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/prediction-markets/polymarket-agent-mcp',
    usageExample: '> connect polymarket MCP\n> trade via AI agent\n> query market data',
    detailedDescription: 'Polymarket Agent MCP provides Model Context Protocol integration for AI agents to access Polymarket. Query market data, analyze order books, execute trades, and manage positions through standardized MCP interfaces — enabling any AI agent to participate in decentralized prediction markets.',
  },
  {
    id: 'polymarket-agent-tools',
    name: 'polymarket-agent-tools',
    author: 'Polymarket Tools',
    authorHandle: '@polymarket_tools',
    tag: 'Skill',
    category: 'tools',
    description: 'Specialized tools for Polymarket agents. Market scanners, order builders, and position analytics for autonomous prediction market trading.',
    downloads: 15600,
    stars: 1167,
    installCommand: '> install the polymarket-agent-tools skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/prediction-markets/polymarket-agent-tools',
    usageExample: '> scan polymarket\n> build orders\n> analyze positions',
    detailedDescription: 'Polymarket Agent Tools provides specialized utilities for autonomous prediction market agents. Scan all active markets with custom filters, build complex order sequences for position entry and exit, and analyze portfolio exposure with profit/loss tracking and risk metrics for event contract trading.',
  },
  {
    id: 'polymarket-agent',
    name: 'polymarket-agent',
    author: 'Polymarket AI',
    authorHandle: '@polymarket_ai',
    tag: 'Skill',
    category: 'tools',
    description: 'Autonomous Polymarket trading agent. AI-driven market analysis, position sizing, and execution for decentralized event contracts.',
    downloads: 26700,
    stars: 1876,
    installCommand: '> install the polymarket-agent skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/prediction-markets/polymarket-agent',
    usageExample: '> deploy polymarket agent\n> analyze markets\n> auto-trade events',
    detailedDescription: 'Polymarket Agent is an autonomous AI trading system for decentralized prediction markets. Analyze market conditions using on-chain data and external signals, dynamically size positions based on edge and bankroll, and execute trades on Polymarket with risk management including stop-losses and time-based exits.',
  },
  {
    id: 'polymarket-api',
    name: 'polymarket-api',
    author: 'Polymarket Dev',
    authorHandle: '@polymarket_dev',
    tag: 'Skill',
    category: 'analytics',
    description: 'Full Polymarket API integration. Market data, order management, portfolio tracking, and historical analytics for decentralized prediction markets.',
    downloads: 18900,
    stars: 1345,
    installCommand: '> install the polymarket-api skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/prediction-markets/polymarket-api',
    usageExample: '> query market data\n> manage orders\n> track portfolio',
    detailedDescription: 'Polymarket API provides complete access to the decentralized prediction market platform. Query market metadata, prices, and liquidity. Place and cancel orders, track portfolio positions and PnL, and access historical data for backtesting prediction market strategies.',
  },
];